// The confirm card's three taps — T56 (10 Sep 2026), agents doc § 7.3.
//
// THE ONLY WRITE IS THE RECRUITER'S TAP. kadmon proposed the card and the service stored it; nothing
// happened. Confirm asks the service to apply THAT stored card — its steps as this app drew them,
// never anything this app sends back — as the recruiter who tapped. Dismiss records that they
// did not want it. Settle is the poll for a card whose last step waits on the worker (a new
// person's record must exist before they can be put on a Search), the same shape as the import's.
//
// EVERY CONFIRM HERE IS A TAP (T595, Natanel, 2 Nov 2026: "Remove spoken yes from Talk"). The card's
// button is the only caller, so every request says `tapped: true`, which the service requires of a
// card that removes something.
//
// Every one ends in a refetch (`useConsoleMutation`), and a wide one: a card may have written a
// note, a stage, a hold, a send request, a code — so the keys are the union of what those forms
// invalidate (`KEYS.action`), plus the chat the card is drawn in, which is where the outcome
// shows.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ActionResponse } from '@vera/bff';
import { confirmedHere } from '../app/sign-out.ts';
import { confirmEach } from '../card-groups.ts';
import { ApiError, API_PREFIX, type RequestFn } from './core.ts';
import { KEYS, useConsoleMutation } from './mutation.ts';
import { useRequest } from './provider.tsx';

export const actionsApi = (request: RequestFn) => ({
  confirm: async (actionId: string, file?: string) => {
    const { card } = await request<ActionResponse>(`${API_PREFIX}/actions/${actionId}/confirm`, {
      method: 'POST',
      body: { tapped: true, ...(file ? { file } : {}) },
    });
    // T570a review round 1 (R9): this tab's own Confirm of a sign_out signs it out (sign-out.ts).
    confirmedHere(card);
    return card;
  },
  dismiss: async (actionId: string) =>
    (await request<ActionResponse>(`${API_PREFIX}/actions/${actionId}/dismiss`, { method: 'POST' }))
      .card,
  settle: async (actionId: string) =>
    (await request<ActionResponse>(`${API_PREFIX}/actions/${actionId}/settle`, { method: 'POST' }))
      .card,
});

export const useConfirmAction = () =>
  useConsoleMutation(
    (request, actionId: string) => actionsApi(request).confirm(actionId),
    KEYS.action,
  );

/**
 * T360 E3 3b: the button's Confirm for a Drive card that saves a file attached in the chat: the
 * file goes up again with the tap, in base64, since the brain keeps no file.
 */
export const useConfirmCardWithFile = () =>
  useConsoleMutation(
    (request, v: { actionId: string; file: string }) =>
      actionsApi(request).confirm(v.actionId, v.file),
    KEYS.action,
  );

/** Somebody else decided the card first, or it is gone: passed over, never a failure of the group. */
const decidedElsewhere = (error: unknown): boolean =>
  error instanceof ApiError && (error.code === 'conflict' || error.code === 'not_found');

/**
 * T76: one Confirm for a group — many writes of a kind, drawn as one card (card-groups.ts). Each
 * card is still applied by its own request, in order; the chat is refetched after each, so the
 * card shows how far the group has got, and the wide refetch a Confirm ends in runs ONCE, after
 * the last — eight of them would refetch every list in the app eight times.
 */
export function useConfirmGroup() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: readonly string[]) => {
      try {
        return await confirmEach(
          ids,
          (id) => actionsApi(request).confirm(id),
          decidedElsewhere,
          () => queryClient.invalidateQueries({ queryKey: ['chat'] }),
        );
      } finally {
        await Promise.all(
          KEYS.action.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
        );
      }
    },
  });
}

/** T76: Dismiss all — each card still up, one after another; one already decided is passed over. */
export function useDismissGroup() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: readonly string[]) => {
      try {
        return await confirmEach(ids, (id) => actionsApi(request).dismiss(id), decidedElsewhere);
      } finally {
        await Promise.all(KEYS.chat.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
      }
    },
  });
}

export const useDismissAction = () =>
  useConsoleMutation(
    (request, actionId: string) => actionsApi(request).dismiss(actionId),
    KEYS.chat,
  );

/**
 * The settle poll refetches the chat alone: it runs every few seconds while a card waits, and a
 * poll that refetched every list in the app each time would be twenty requests per tick for a
 * card that has not moved. The card view invalidates the wide group once, when the settle says
 * the card is no longer waiting.
 */
export const useSettleAction = () =>
  useConsoleMutation(
    (request, actionId: string) => actionsApi(request).settle(actionId),
    KEYS.chat,
  );
